import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Edit, Trash2, MapPin, ExternalLink, Loader2, Archive, RotateCcw, Search } from 'lucide-react'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { PageHeader } from '@/components/layout/page-header'
import { type ColumnDef } from '@tanstack/react-table'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { DataTable } from '@/components/ui/data-table'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog'
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form'
import { projects as projectsApi } from '@/utils/api'
import { getStatusLabel } from '@/utils/formatters'
import { useProjects } from '@/hooks/useProjects'
import { useUsers } from '@/hooks/useUsers'
import { projectSchema, type ProjectInput } from '@/schemas/project'

interface ProjectData {
  id: number; name: string; address?: string; client_name?: string; status: string
  latitude?: number; longitude?: number; radius_km?: number; notes?: string; diary_recipient?: string
  bauleiter_id?: number | null
}

interface UserOption { id: string; name: string; role: string; is_active: boolean }

// Wer als Bauleiter eines Bauvorhabens eingetragen werden kann (wird Kanal-Admin in Mattermost)
const BAULEITER_ROLES = ['bauleiter', 'admin']

const statusVariant = (s: string) => s === 'active' ? 'success' as const : s === 'paused' ? 'warning' as const : s === 'completed' ? 'destructive' as const : 'secondary' as const

function ProjectForm({ project, onClose, onSuccess }: { project: ProjectData | null; onClose: () => void; onSuccess: () => void }) {
  // Fields not covered by projectSchema: status, notes, diary_recipient, and the GPS location block
  const [status, setStatus] = useState(project?.status || 'active')
  const [notes, setNotes] = useState(project?.notes || '')
  const [diaryRecipient, setDiaryRecipient] = useState(project?.diary_recipient || '')
  const [bauleiterId, setBauleiterId] = useState(project?.bauleiter_id ? String(project.bauleiter_id) : '')
  const { data: allUsers = [] } = useUsers()
  const bauleiterOptions = (allUsers as UserOption[])
    .filter(u => BAULEITER_ROLES.includes(u.role) && (u.is_active || String(u.id) === bauleiterId))
    .sort((a, b) => (a.role === b.role ? a.name.localeCompare(b.name) : a.role === 'bauleiter' ? -1 : 1))
  const [location, setLocation] = useState({ latitude: project?.latitude ? String(project.latitude) : '', longitude: project?.longitude ? String(project.longitude) : '', radius: (project?.radius_km || 0.5) * 1000 })
  const [googleMapsLink, setGoogleMapsLink] = useState('')
  const [resolvingUrl, setResolvingUrl] = useState(false)

  const form = useForm<ProjectInput>({
    resolver: zodResolver(projectSchema),
    defaultValues: {
      name: project?.name || '',
      address: project?.address || '',
      client: project?.client_name || '',
    },
  })

  const { formState: { isSubmitting }, watch } = form
  const clientName = watch('client')

  const parseGoogleMapsUrl = (url: string) => {
    const patterns = [/@(-?\d+\.?\d*),(-?\d+\.?\d*)/, /[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/, /[?&]ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/, /\/(-?\d+\.\d+),(-?\d+\.\d+)/]
    for (const p of patterns) { const m = url.match(p); if (m) { const [lat, lng] = [parseFloat(m[1]), parseFloat(m[2])]; if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) return { latitude: lat, longitude: lng } } }
    return null
  }

  const handleResolveUrl = async () => {
    if (!googleMapsLink.trim()) return
    const direct = parseGoogleMapsUrl(googleMapsLink)
    if (direct) { setLocation(l => ({ ...l, latitude: String(direct.latitude), longitude: String(direct.longitude) })); toast.success('Koordinaten extrahiert'); return }
    setResolvingUrl(true)
    try { const r = await projectsApi.resolveMapsUrl(googleMapsLink); if (r.data?.latitude && r.data?.longitude) { setLocation(l => ({ ...l, latitude: String(r.data.latitude), longitude: String(r.data.longitude) })); toast.success(`Koordinaten: ${r.data.latitude.toFixed(6)}, ${r.data.longitude.toFixed(6)}`) } else toast.error('Keine Koordinaten gefunden') }
    catch (e) { toast.error(e instanceof Error ? e.message : 'Fehler beim Auflösen') }
    finally { setResolvingUrl(false) }
  }

  const handleSubmit = async (data: ProjectInput) => {
    try {
      const payload = {
        name: data.name,
        address: data.address,
        client_name: data.client,
        status,
        notes,
        diary_recipient: diaryRecipient,
        bauleiter_id: bauleiterId ? Number(bauleiterId) : null,
        location: location.latitude && location.longitude ? { latitude: parseFloat(location.latitude), longitude: parseFloat(location.longitude), radius: location.radius || 500 } : null,
      }
      if (project) await projectsApi.update(project.id, payload); else await projectsApi.create(payload)
      onSuccess()
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Fehler') }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
        <FormField control={form.control} name="name" render={({ field }) => (
          <FormItem><FormLabel>Name *</FormLabel><FormControl><Input placeholder="z.B. Neubau Einfamilienhaus" {...field} /></FormControl><FormMessage /></FormItem>
        )} />

        <FormField control={form.control} name="address" render={({ field }) => (
          <FormItem><FormLabel>Adresse</FormLabel><FormControl><Input placeholder="Strasse, PLZ Ort" {...field} /></FormControl><FormMessage /></FormItem>
        )} />

        <FormField control={form.control} name="client" render={({ field }) => (
          <FormItem><FormLabel>Auftraggeber</FormLabel><FormControl><Input placeholder="Name des Auftraggebers" {...field} /></FormControl><FormMessage /></FormItem>
        )} />

        <div className="space-y-2"><label className="text-sm font-medium">Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm">
            <option value="active">Aktiv</option><option value="paused">Pausiert</option><option value="completed">Abgeschlossen</option><option value="archived">Archiviert</option>
          </select>
        </div>

        <div className="space-y-2"><label className="text-sm font-medium">Bauleiter</label>
          <select value={bauleiterId} onChange={(e) => setBauleiterId(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm">
            <option value="">– kein Bauleiter –</option>
            {bauleiterOptions.map(u => <option key={u.id} value={String(u.id)}>{u.name}{u.role === 'admin' ? ' (Admin)' : ''}</option>)}
          </select>
          <p className="text-xs text-muted-foreground">Wird Admin im Baustellen-Kanal im Chat und kann dort Kollegen einladen.</p>
        </div>

        <div className="space-y-2"><label className="text-sm font-medium">Notizen</label><textarea className="flex min-h-[80px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm" value={notes} onChange={(e) => setNotes(e.target.value)} /></div>

        <div className="space-y-3 rounded-lg border bg-amber-50 p-4 dark:bg-amber-950">
          <label className="font-medium">Daten Bautagebuch</label>
          <div className="space-y-2"><label className="text-sm font-medium">Empfaenger</label><textarea className="flex min-h-[80px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm" value={diaryRecipient} onChange={(e) => setDiaryRecipient(e.target.value)} placeholder={clientName ? `Standard: ${clientName}` : 'Adresse'} /></div>
        </div>

        <div className="space-y-4 rounded-lg border bg-muted/50 p-4">
          <div className="flex items-center gap-2"><MapPin className="h-5 w-5 text-primary-500" /><label className="font-medium">GPS-Standort</label></div>
          <div className="space-y-2"><label className="text-sm font-medium">Google Maps Link</label><Input value={googleMapsLink} onChange={(e) => setGoogleMapsLink(e.target.value)} placeholder="maps.app.goo.gl/..." />
            <Button type="button" size="sm" onClick={handleResolveUrl} disabled={resolvingUrl || !googleMapsLink.trim()}>
              {resolvingUrl ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />} Link auflösen
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><label className="text-sm font-medium">Breitengrad</label><Input type="number" step="any" value={location.latitude} onChange={(e) => setLocation(l => ({ ...l, latitude: e.target.value }))} /></div>
            <div className="space-y-2"><label className="text-sm font-medium">Laengengrad</label><Input type="number" step="any" value={location.longitude} onChange={(e) => setLocation(l => ({ ...l, longitude: e.target.value }))} /></div>
          </div>
          <div className="space-y-2"><label className="text-sm font-medium">Radius: <span className="text-primary-600">{location.radius}m</span></label><input type="range" min="50" max="2000" step="50" value={location.radius} onChange={(e) => setLocation(l => ({ ...l, radius: parseInt(e.target.value) }))} className="w-full" /></div>
          {location.latitude && location.longitude && (
            <div className="flex items-center justify-between rounded-lg border bg-card p-3">
              <span className="font-mono text-sm">{Number(location.latitude).toFixed(6)}, {Number(location.longitude).toFixed(6)}</span>
              <a href={`https://www.google.com/maps?q=${location.latitude},${location.longitude}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm text-primary-500 hover:underline"><MapPin className="h-4 w-4" />Maps</a>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-4"><Button variant="outline" type="button" onClick={onClose}>Abbrechen</Button><Button type="submit" disabled={isSubmitting}>{isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />} Speichern</Button></div>
      </form>
    </Form>
  )
}

export default function Bauvorhaben() {
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState('active')
  const [search, setSearch] = useState('')
  const [editModal, setEditModal] = useState<{ open: boolean; project: ProjectData | null }>({ open: false, project: null })
  const [deleteModal, setDeleteModal] = useState<{ open: boolean; project: ProjectData | null }>({ open: false, project: null })
  const [archiveModal, setArchiveModal] = useState<{ open: boolean; project: ProjectData | null }>({ open: false, project: null })
  const [unarchiveModal, setUnarchiveModal] = useState<{ open: boolean; project: ProjectData | null }>({ open: false, project: null })

  const queryParams = {
    status: statusFilter === 'all' ? undefined : statusFilter,
    includeArchived: statusFilter === 'all' || statusFilter === 'archived' ? 'true' : undefined,
  }
  const { data: projects = [], isLoading } = useProjects(queryParams)
  const { data: allUsers = [] } = useUsers()
  const userName = (id?: number | null) => id ? (allUsers as UserOption[]).find(u => String(u.id) === String(id))?.name : undefined

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['projects'] })

  const deleteMutation = useMutation({
    mutationFn: (p: ProjectData) => projectsApi.delete(p.id, p.status === 'archived'),
    onSuccess: () => { toast.success('Gelöscht'); setDeleteModal({ open: false, project: null }); invalidate() },
    onError: (e, p) => {
      if (e instanceof Error && e.message?.includes('archivieren')) {
        setDeleteModal({ open: false, project: null }); setArchiveModal({ open: true, project: p })
      } else toast.error(e instanceof Error ? e.message : 'Fehler')
    },
  })

  const archiveMutation = useMutation({
    mutationFn: (id: number) => projectsApi.archive(id),
    onSuccess: () => { toast.success('Archiviert'); setArchiveModal({ open: false, project: null }); invalidate() },
    onError: (e) => { toast.error(e instanceof Error ? e.message : 'Fehler') },
  })

  const unarchiveMutation = useMutation({
    mutationFn: (id: number) => projectsApi.unarchive(id),
    onSuccess: () => { toast.success('Wiederhergestellt'); setUnarchiveModal({ open: false, project: null }); invalidate() },
    onError: (e) => { toast.error(e instanceof Error ? e.message : 'Fehler') },
  })

  const handleDelete = () => { if (!deleteModal.project) return; deleteMutation.mutate(deleteModal.project) }
  const handleArchive = () => { if (!archiveModal.project) return; archiveMutation.mutate(archiveModal.project.id) }
  const handleUnarchive = () => { if (!unarchiveModal.project) return; unarchiveMutation.mutate(unarchiveModal.project.id) }

  return (
    <div>
      <PageHeader title="Bauvorhaben" description={`${projects.length} Bauvorhaben`} actions={<Button onClick={() => setEditModal({ open: true, project: null })}><Plus className="h-4 w-4" /> Neues Bauvorhaben</Button>} />
      <Card className="mb-6"><CardContent className="flex flex-wrap gap-4 py-4">
        <div className="relative min-w-[200px] max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Suche..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="flex h-9 w-48 rounded-md border border-input bg-white px-3 py-1 text-sm">
          <option value="active">Aktiv</option><option value="paused">Pausiert</option><option value="completed">Abgeschlossen</option><option value="archived">Archiviert</option><option value="all">Alle</option>
        </select>
      </CardContent></Card>

      {isLoading ? <div className="space-y-2">{Array.from({length:4}).map((_,i) => <Skeleton key={i} className="h-14 w-full" />)}</div> : (
        <DataTable
          columns={[
            { accessorKey: 'name', header: 'Name', cell: ({ row }) => <span className="font-medium">{row.original.name}</span> },
            { accessorKey: 'address', header: 'Adresse', cell: ({ row }) => row.original.address || '-' },
            { accessorKey: 'client_name', header: 'Auftraggeber', cell: ({ row }) => row.original.client_name || '-' },
            { id: 'bauleiter', header: 'Bauleiter', accessorFn: (p) => userName(p.bauleiter_id) || '', cell: ({ row }) => userName(row.original.bauleiter_id) || '-' },
            { accessorKey: 'status', header: 'Status', cell: ({ row }) => <Badge variant={statusVariant(row.original.status)}>{getStatusLabel(row.original.status)}</Badge> },
            { id: 'gps', header: 'GPS', enableSorting: false, cell: ({ row }) => row.original.latitude && row.original.longitude ? <a href={`https://www.google.com/maps?q=${row.original.latitude},${row.original.longitude}`} target="_blank" rel="noopener noreferrer" className="text-primary-500"><MapPin className="h-5 w-5" /></a> : '-' },
            { id: 'actions', header: 'Aktionen', enableSorting: false, cell: ({ row }) => {
              const p = row.original;
              return <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                <Button variant="ghost" size="icon" onClick={() => setEditModal({ open: true, project: p })}><Edit className="h-4 w-4" /></Button>
                {p.status === 'archived' ? <Button variant="ghost" size="icon" className="text-green-600" onClick={() => setUnarchiveModal({ open: true, project: p })}><RotateCcw className="h-4 w-4" /></Button> : <Button variant="ghost" size="icon" className="text-amber-600" onClick={() => setArchiveModal({ open: true, project: p })}><Archive className="h-4 w-4" /></Button>}
                <Button variant="ghost" size="icon" className="text-destructive" onClick={() => setDeleteModal({ open: true, project: p })}><Trash2 className="h-4 w-4" /></Button>
              </div>;
            }},
          ] as ColumnDef<ProjectData>[]}
          data={search ? projects.filter((p: ProjectData) => p.name?.toLowerCase().includes(search.toLowerCase()) || p.address?.toLowerCase().includes(search.toLowerCase()) || p.client_name?.toLowerCase().includes(search.toLowerCase())) : projects}
          emptyText="Keine Bauvorhaben"
        />
      )}

      <Dialog open={editModal.open} onOpenChange={(o) => !o && setEditModal({ open: false, project: null })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle>{editModal.project ? 'Bauvorhaben bearbeiten' : 'Neues Bauvorhaben'}</DialogTitle></DialogHeader>
          <ProjectForm project={editModal.project} onClose={() => setEditModal({ open: false, project: null })} onSuccess={() => { setEditModal({ open: false, project: null }); invalidate(); toast.success(editModal.project ? 'Aktualisiert' : 'Erstellt') }} />
        </DialogContent>
      </Dialog>

      {[{ state: deleteModal, set: setDeleteModal, title: deleteModal.project?.status === 'archived' ? 'Endgültig löschen' : 'Bauvorhaben löschen', msg: deleteModal.project?.status === 'archived' ? `"${deleteModal.project?.name}" und ALLE verknuepften Daten endgültig löschen?` : `"${deleteModal.project?.name}" löschen?`, action: handleDelete, variant: 'destructive' as const, btn: 'Löschen' },
        { state: archiveModal, set: setArchiveModal, title: 'Archivieren', msg: `"${archiveModal.project?.name}" archivieren? Daten bleiben erhalten.`, action: handleArchive, variant: 'default' as const, btn: 'Archivieren' },
        { state: unarchiveModal, set: setUnarchiveModal, title: 'Wiederherstellen', msg: `"${unarchiveModal.project?.name}" wiederherstellen?`, action: handleUnarchive, variant: 'default' as const, btn: 'Wiederherstellen' },
      ].map((m, i) => (
        <Dialog key={i} open={m.state.open} onOpenChange={(o) => !o && m.set({ open: false, project: null })}>
          <DialogContent><DialogHeader><DialogTitle>{m.title}</DialogTitle><DialogDescription>{m.msg}</DialogDescription></DialogHeader>
            <DialogFooter><Button variant="outline" onClick={() => m.set({ open: false, project: null })}>Abbrechen</Button><Button variant={m.variant} onClick={m.action}>{m.btn}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      ))}
    </div>
  )
}
